/* ==============================================================
   obf-pdp-thumbsync.js — 2026-09-09

   Reported: "when I change the image, the media gallery below the main
   image does not move."

   WHY IT DOES NOT MOVE
   --------------------
   The theme has two different thumbnail classes and they are easy to
   confuse:

     .is-active           = "this thumb belongs to the selected COLOUR".
                            It is a visibility filter, not a highlight.
                            snippets/product-thumbnail.liquid ends with
                            `unless hide_variants / assign is_active = true`,
                            and hide_variants is false on this theme, so
                            EVERY thumb is is-active. Measured: [1,1,1,1,1,1,1].

     .is-initial-selected = the actual current-thumb ring
                            (product.css: .is-initial-selected:after sets the
                            inset box-shadow). Liquid writes it ONCE, onto the
                            variant's featured media, and nothing ever moves it.

   So on load thumb 1 gets the ring and keeps it forever. Driving the main
   image to index 6 leaves the ring on thumb 1 and scrollLeft at 0, which is
   exactly what "the gallery below does not move" describes.

   There is no theme JS that syncs them because the gallery is replaced at
   runtime by the StarApps Variant Image Automator: it clones the strip into
   <scroll-shadow class="starapps"> and drops whatever the theme had wired.

   WHAT THIS DOES
   --------------
   Listens to Flickity's own `change` event on #Product-Slider (the single
   source of truth for which image is showing) and then:
     1. moves .is-initial-selected onto the matching thumb, and
     2. scrolls the strip so that thumb is in view.

   It binds to the SLIDER, never to the thumbnails, so it keeps working after
   StarApps rebuilds the strip on a colour change. Thumb ids encode the media
   id (Thumb-<section>-<mediaId>) and so do slide ids, so the two are matched
   on media id rather than on position — position is unreliable because the
   strip is filtered per colour while the slider is not always.

   Presentation and behaviour only. No markup is added, no theme file is
   edited, and if this script fails the gallery behaves exactly as it does
   today.
   ============================================================== */
(function () {
  'use strict';

  var SEL_RING = 'is-initial-selected';

  function visible(el) {
    return !!el && el.offsetWidth > 0 && el.offsetHeight > 0;
  }

  /* There are three #Product-Thumbnails in the DOM on this theme (the
     theme's own, the StarApps clone, and a stale copy). Only one is ever
     rendered, so pick by visibility rather than by id or order. */
  function strip() {
    var all = document.querySelectorAll('.product-thumbnail-container');
    for (var i = 0; i < all.length; i++) if (visible(all[i])) return all[i];
    return null;
  }

  /* Thumb-<sectionId>-<mediaId> and Slide-<sectionId>-<mediaId> share the
     trailing media id. Take the last dash-separated chunk. */
  function mediaId(el) {
    if (!el || !el.id) return null;
    var m = el.id.match(/(\d+)$/);
    return m ? m[1] : null;
  }

  function sync(slider, flkty) {
    var box = strip();
    if (!box || !flkty) return;

    var cell = flkty.selectedElement;
    if (!cell) return;
    var want = mediaId(cell);

    var thumbs = box.querySelectorAll('.product-thumbnail');
    var target = null;

    for (var i = 0; i < thumbs.length; i++) {
      var t = thumbs[i];
      var hit = want && mediaId(t) === want;
      /* Fall back to index only if the ids do not line up, so a theme
         change that renames the ids degrades instead of breaking. */
      if (!want && i === flkty.selectedIndex) hit = true;
      if (hit) target = t;
      t.classList.toggle(SEL_RING, !!hit);
    }

    if (!target && thumbs[flkty.selectedIndex]) {
      target = thumbs[flkty.selectedIndex];
      target.classList.add(SEL_RING);
    }
    if (!target) return;

    /* Centre the active thumb in the strip. scrollIntoView would also
       scroll the PAGE vertically to the gallery, which is jarring while
       the shopper is reading further down, so compute scrollLeft by hand
       and clamp it to the scrollable range. */
    var max = box.scrollWidth - box.clientWidth;
    if (max <= 0) return;

    var left = target.offsetLeft - (box.clientWidth - target.offsetWidth) / 2;
    left = Math.max(0, Math.min(left, max));

    if (Math.abs(box.scrollLeft - left) < 2) return;

    /* scroll-snap-type: x mandatory (obf-pdp-v9.css) OVERRIDES a programmatic
       scroll: the snap engine re-runs straight after scrollTo and yanks the
       strip back to the nearest snap point, which from a standing start is
       always 0. Measured: target offsetLeft 370, computed left 247,
       resulting scrollLeft 0.

       So suspend snapping for the duration of the scroll, then restore it,
       because it is still wanted for the shopper's own finger-drag. The
       restore is on a timer rather than the scrollend event, which Safari
       did not support until 2024. */
    /* Capture the ORIGINAL value once and stash it on the node. Reading
       box.style each time is wrong: a second call while a scroll is still in
       flight would capture the 'none' we ourselves just set and then restore
       that, leaving snapping permanently off for the shopper's own drag. */
    if (box.obfSnapSaved === undefined) box.obfSnapSaved = box.style.scrollSnapType || '';
    box.style.scrollSnapType = 'none';

    try {
      box.scrollTo({ left: left, behavior: 'smooth' });
    } catch (e) {
      box.scrollLeft = left; /* older Safari has no options object */
    }

    clearTimeout(box.obfSnapTimer);
    box.obfSnapTimer = setTimeout(function () {
      box.style.scrollSnapType = box.obfSnapSaved;
      box.obfSnapSaved = undefined;
    }, 500);
  }

  /* Module-level, NOT an expando on the node. StarApps replaces both
     #Product-Slider and the gallery container at runtime, so a flag stored
     on the element disappears with it and we would silently never rebind
     (measured: obfThumbSyncBound read back false after a colour change,
     while the ring still moved because a STALE listener on the old node was
     doing it). Tracking the Flickity instance instead means a swap is
     detected as "different object" and we rebind to the live one. */
  var boundFlkty = null;

  function bind() {
    var slider = document.querySelector('#Product-Slider');
    if (!slider || !window.Flickity) return false;

    var flkty = window.Flickity.data(slider);
    if (!flkty) return false;
    if (flkty === boundFlkty) return true;
    boundFlkty = flkty;

    flkty.on('change', function () { sync(slider, flkty); });
    /* settle fires after the animation, which is when the strip should
       land on its final position if a drag overshot. */
    flkty.on('settle', function () { sync(slider, flkty); });

    sync(slider, flkty);
    return true;
  }

  /* Flickity is initialised by the theme after DOM ready, and StarApps
     re-runs later still. Poll briefly rather than racing either of them. */
  function start() {
    bind();
    /* Keep polling for the LIFE of the page rather than stopping once bound.
       StarApps can rebuild the gallery at any time (every colour change), and
       bind() is a cheap no-op while the instance is unchanged. A one-shot
       poll that cleared itself was why the strip stopped following after the
       first rebuild. */
    if (window.obfThumbSyncPoll) return;
    window.obfThumbSyncPoll = setInterval(bind, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* A colour change makes StarApps rebuild the strip, which throws away the
     ring we just set. Re-sync when the subtree changes, debounced so a
     rebuild of 7 thumbs does not run this 7 times. */
  var pending = null;
  var mo = new MutationObserver(function () {
    clearTimeout(pending);
    pending = setTimeout(function () {
      var slider = document.querySelector('#Product-Slider');
      if (slider && window.Flickity) {
        var f = window.Flickity.data(slider);
        if (f) sync(slider, f);
      }
    }, 120);
  });

  /* Observe a STABLE ancestor. .product-gallery-container is itself
     replaced by StarApps, and an observer attached to a detached node never
     fires again. .product-grid-container survives the swap. */
  var host = document.querySelector('.product-grid-container') ||
             document.querySelector('.product-gallery-container');
  if (host) mo.observe(host, { childList: true, subtree: true });

  /* The theme editor re-renders the whole section. */
  document.addEventListener('shopify:section:load', start);
})();
